import type { Proposta, PropostaItem } from '@/types'
import { extractServices } from '@/utils/dealCalculations'
import { mergePlans, type MergedPlan } from '@/utils/planMerger'
import { safeParseJson } from '@/utils/propostaPublicaUtils'

export type LegacyPlanItem = MergedPlan & { id?: string; comboGroupId?: string }

export type PropostaV2Dados = {
  condicaoPagamento: { vigencia: number }
  termos?: string
  isCombo?: boolean
  itens?: PropostaItem[]
}

function mapItemToLegacy(item: PropostaItem): Record<string, unknown> {
  const name = item.descricao || item.nome || item.name || 'Plano'
  const total = Number(item.total ?? 0)
  const dur = Number(item.duration ?? item.pricing?.installments ?? item.pricing?.duracao ?? 12)
  const isRec = item.tipo === 'plano' || Boolean(item.pricing?.recorrente)
  const services = extractServices(item)

  return {
    ...item,
    nome: name,
    name,
    descricao: item.description,
    description: item.description,
    tipo: isRec ? 'recorrente' : 'setup',
    valor: total,
    duration: dur,
    services,
    pricing: item.pricing ?? {
      total,
      entry: 0,
      installments: dur,
      installmentValue: isRec ? total / Math.max(dur, 1) : total,
    },
  }
}

export function parsePropostaV2Dados(proposta: Proposta): PropostaV2Dados {
  const parsed = safeParseJson((proposta as Proposta & { dados?: string }).dados)
  const condicao = (parsed.condicaoPagamento as { vigencia?: number }) || {}
  return {
    condicaoPagamento: { vigencia: Number(condicao.vigencia ?? proposta.validade ?? 30) },
    termos: String(parsed.termos ?? proposta.observacoes ?? ''),
    isCombo: Boolean(parsed.isCombo),
    itens: (parsed.itens as PropostaItem[]) || undefined,
  }
}

export function buildLegacyPlanItems(proposta: Proposta): LegacyPlanItem[] {
  const dados = parsePropostaV2Dados(proposta)
  const rawItems = (proposta.itens?.length ? proposta.itens : proposta.items) ?? dados.itens ?? []
  if (rawItems.length === 0) return []

  const processed: LegacyPlanItem[] = []
  const groupedCombos: Record<string, PropostaItem[]> = {}

  rawItems.forEach((item) => {
    if (item.comboGroupId) {
      if (!groupedCombos[item.comboGroupId]) groupedCombos[item.comboGroupId] = []
      groupedCombos[item.comboGroupId].push(item)
    } else {
      const legacy = mapItemToLegacy(item)
      const normalized = mergePlans([legacy], proposta.desconto ?? 0, String(legacy.nome || 'Plano'))
      if (normalized) processed.push({ ...normalized, id: item.id })
    }
  })

  Object.keys(groupedCombos).forEach((comboId) => {
    const comboPlans = groupedCombos[comboId].map(mapItemToLegacy)
    const merged = mergePlans(comboPlans, proposta.desconto ?? 0, `Combo ${comboId} Agrada`)
    if (!merged) return
    if (comboPlans[0]?.comboFinalPrice != null) {
      const comboPrice = Number(comboPlans[0].comboFinalPrice)
      merged.valor = comboPrice
      merged.pricing.total = comboPrice
      if (merged.tipo === 'recorrente') {
        merged.pricing.installmentValue = comboPrice
      }
    }
    merged.comboGroupId = comboId
    processed.push(merged)
  })

  return processed
}
